import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { CATEGORIES } from "@/lib/categories";

export function ProductAdmin({ token }) {
  const [items, setItems] = useState(null);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ name: "", brand: "", category: CATEGORIES[0], price: "", description: "", image_url: "", vendor_email: "" });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const headers = { Authorization: `Bearer ${token}` };

  const load = async () => {
    try {
      const { data } = await api.get("/admin/products", { headers });
      setItems(data);
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    setMsg("");
    try {
      await api.post("/admin/products", { ...form, price: parseFloat(form.price) }, { headers });
      setMsg("Product added to the shop.");
      setForm({ name: "", brand: "", category: CATEGORIES[0], price: "", description: "", image_url: "", vendor_email: "" });
      load();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const setStatus = async (id, status) => {
    await api.patch(`/admin/products/${id}`, { status }, { headers });
    setItems((l) => l.map((x) => (x.id === id ? { ...x, status } : x)));
  };

  const remove = async (id) => {
    await api.delete(`/admin/products/${id}`, { headers });
    setItems((l) => l.filter((x) => x.id !== id));
  };

  return (
    <div data-testid="product-admin">
      <form onSubmit={add} className="grid grid-cols-1 gap-4 rounded-2xl border border-beige/60 bg-white/80 p-6 sm:grid-cols-2" data-testid="admin-add-product-form">
        <p className="sm:col-span-2 text-[11px] uppercase tracking-[0.24em] text-fog">Add a product (goes live immediately)</p>
        <input required className="field" placeholder="Product name" value={form.name} onChange={set("name")} data-testid="admin-product-name-input" />
        <input required className="field" placeholder="Brand / business" value={form.brand} onChange={set("brand")} data-testid="admin-product-brand-input" />
        <select className="field" value={form.category} onChange={set("category")} data-testid="admin-product-category-select">
          {CATEGORIES.map((c) => (
            <option key={c}>{c}</option>
          ))}
        </select>
        <input required type="number" min="0.5" step="0.01" className="field" placeholder="Price (USD)" value={form.price} onChange={set("price")} data-testid="admin-product-price-input" />
        <input required type="url" className="field sm:col-span-2" placeholder="Image URL (https://…)" value={form.image_url} onChange={set("image_url")} data-testid="admin-product-image-input" />
        <textarea className="field sm:col-span-2" rows={2} placeholder="Short description" value={form.description} onChange={set("description")} data-testid="admin-product-description-input" />
        <input type="email" className="field" placeholder="Vendor email (optional)" value={form.vendor_email} onChange={set("vendor_email")} data-testid="admin-product-email-input" />
        <button type="submit" disabled={busy} className="btn btn-dark disabled:opacity-60" data-testid="admin-product-add-button">
          {busy ? "Adding…" : "Add Product"}
        </button>
        {msg && <p className="sm:col-span-2 text-sm text-green-700">{msg}</p>}
        {error && <p className="sm:col-span-2 text-sm text-red-700">{error}</p>}
      </form>

      <div className="mt-8 space-y-3" data-testid="admin-product-list">
        {items === null && <p className="text-sm text-fog">Loading…</p>}
        {items && items.length === 0 && <p className="rounded-2xl border border-beige/60 bg-white/70 p-6 text-sm text-clay">No products yet.</p>}
        {items &&
          items.map((p) => (
            <div key={p.id} className="flex flex-wrap items-center gap-4 rounded-2xl border border-beige/60 bg-white/80 p-4" data-testid="admin-product-row">
              <img src={p.image_url} alt={p.name} className="h-14 w-14 rounded-xl object-cover" />
              <div className="min-w-[180px] flex-1">
                <p className="font-serif text-lg text-ink">{p.name}</p>
                <p className="text-xs text-fog">
                  {p.brand} · {p.category} · ${p.price.toFixed(2)}
                </p>
              </div>
              <span
                className={`rounded-full px-3 py-1 text-[10px] uppercase tracking-[0.18em] ${
                  p.status === "approved" ? "bg-blush text-ink" : p.status === "pending" ? "bg-yellow-100 text-yellow-800" : "bg-cream text-fog"
                }`}
                data-testid="admin-product-status"
              >
                {p.status}
              </span>
              <div className="flex gap-2">
                {p.status !== "approved" && (
                  <button onClick={() => setStatus(p.id, "approved")} data-testid="admin-product-approve" className="rounded-full border border-beige px-3 py-1 text-[10px] uppercase tracking-[0.18em] text-clay hover:border-sanddeep hover:text-ink">
                    Approve
                  </button>
                )}
                {p.status === "approved" && (
                  <button onClick={() => setStatus(p.id, "rejected")} data-testid="admin-product-reject" className="rounded-full border border-beige px-3 py-1 text-[10px] uppercase tracking-[0.18em] text-clay hover:border-sanddeep hover:text-ink">
                    Unlist
                  </button>
                )}
                <button onClick={() => remove(p.id)} data-testid="admin-product-delete" className="rounded-full border border-beige px-3 py-1 text-[10px] uppercase tracking-[0.18em] text-red-700 hover:border-red-300">
                  Delete
                </button>
              </div>
            </div>
          ))}
      </div>
    </div>
  );
}
